import { useState } from 'react';
import { GammaLensLogo } from './LandingPage';
import api from '../api';

/* ─── GammaLens Design Tokens ───
   bg:       #111114
   surface:  #1a1a1f
   border:   rgba(255,255,255,0.07)
   text-1:   #f2f2f7
   text-2:   #98989f
   text-3:   #545459
   up:       #34c759
─────────────────────────────── */

export default function AuthPage({ onAuth, onSkip, mode: initialMode = 'login', verifyMessage }) {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [verificationPending, setVerificationPending] = useState(false);
  const [resending, setResending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    try {
      const endpoint = mode === 'signup' ? '/api/auth/signup' : '/api/auth/login';
      const payload = mode === 'signup'
        ? { email, password, display_name: displayName || undefined }
        : { email, password };
      const response = await api.post(endpoint, payload);
      if (mode === 'signup' && response.data.verification_sent) {
        setVerificationPending(true);
        setSuccess('Check your email for a verification link. Verify your email, then log in.');
        return;
      }
      localStorage.setItem('gl_token', response.data.token);
      localStorage.setItem('gl_user', JSON.stringify(response.data.user));
      onAuth(response.data.user, response.data.token);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResending(true);
    setError('');
    try {
      await api.post('/api/auth/resend-verification', { email, password: 'unused' });
      setSuccess('Verification email resent. Check your inbox.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not resend. Try again later.');
    } finally {
      setResending(false);
    }
  };

  const toggleMode = () => {
    setMode(mode === 'signup' ? 'login' : 'signup');
    setError('');
    setSuccess('');
    setVerificationPending(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0d1117',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: "-apple-system,'SF Pro Display','Segoe UI',system-ui,sans-serif",
      padding: '20px',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <style>{`
        @keyframes auth-spin { to { transform: rotate(360deg); } }
        .auth-input:focus { border-color: rgba(52,199,89,0.45) !important; outline: none; box-shadow: 0 0 0 3px rgba(52,199,89,0.07); }
        .auth-input::placeholder { color: #545459; }
        .auth-btn-primary { transition: background .15s, box-shadow .15s; }
        .auth-btn-primary:hover:not(:disabled) { background: #ffffff !important; box-shadow: 0 0 0 4px rgba(242,242,247,.1); }
        .auth-toggle:hover { color: #34c759 !important; }
        .auth-skip:hover { color: #98989f !important; }
      `}</style>

      {/* Background glows */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-200px', right: '-100px', width: '600px', height: '600px', borderRadius: '50%', background: 'radial-gradient(circle,rgba(52,199,89,.05) 0%,transparent 65%)', filter: 'blur(60px)' }} />
        <div style={{ position: 'absolute', bottom: '0', left: '-100px', width: '400px', height: '400px', borderRadius: '50%', background: 'radial-gradient(circle,rgba(48,168,255,.04) 0%,transparent 65%)', filter: 'blur(60px)' }} />
      </div>

      <div style={{ width: '100%', maxWidth: '400px', position: 'relative', zIndex: 1 }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '36px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '11px', marginBottom: '8px' }}>
            <GammaLensLogo size={34} />
            <span style={{ fontSize: '22px', fontWeight: 700, letterSpacing: '-0.3px', color: '#f2f2f7', fontFamily: "'Bricolage Grotesque Variable', system-ui, sans-serif" }}>GammaLens</span>
          </div>
          <p style={{ color: '#545459', fontSize: '13px', margin: 0 }}>See what dealers see</p>
        </div>

        {/* Card */}
        <div style={{
          background: '#161618',
          border: '1px solid rgba(255,255,255,0.07)',
          borderRadius: '18px',
          padding: '32px',
        }}>

          {verificationPending ? (
            /* ── Verification pending ── */
            <div style={{ textAlign: 'center' }}>
              <div style={{
                width: '52px', height: '52px', borderRadius: '14px',
                background: 'rgba(52,199,89,0.08)', border: '1px solid rgba(52,199,89,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px',
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#34c759" strokeWidth="2" strokeLinecap="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </div>
              <h2 style={{ color: '#f2f2f7', fontSize: '19px', fontWeight: 700, margin: '0 0 8px' }}>Check your email</h2>
              <p style={{ color: '#98989f', fontSize: '13px', lineHeight: 1.65, margin: '0 0 6px' }}>We sent a verification link to</p>
              <p style={{ color: '#34c759', fontSize: '14px', fontWeight: 600, margin: '0 0 18px' }}>{email}</p>
              <p style={{ color: '#545459', fontSize: '12px', lineHeight: 1.7, margin: '0 0 22px' }}>Click the link in the email to verify your account, then come back and log in. Link expires in 24 hours.</p>

              {error && <AlertBox type="error">{error}</AlertBox>}
              {success && <AlertBox type="success">{success}</AlertBox>}
              {verifyMessage && <AlertBox type={verifyMessage.type}>{verifyMessage.text}</AlertBox>}

              <button
                onClick={() => { setVerificationPending(false); setMode('login'); setSuccess(''); setError(''); }}
                className="auth-btn-primary"
                style={{ width: '100%', padding: '12px', background: '#f2f2f7', border: 'none', borderRadius: '11px', color: '#111114', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', marginBottom: '10px' }}
              >
                Go to Login
              </button>
              <button
                onClick={handleResendVerification}
                disabled={resending}
                style={{ width: '100%', padding: '10px', background: 'transparent', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '11px', color: '#98989f', fontSize: '13px', fontWeight: 500, cursor: resending ? 'not-allowed' : 'pointer', opacity: resending ? 0.5 : 1, fontFamily: 'inherit' }}
              >
                {resending ? 'Sending...' : "Didn't get the email? Resend"}
              </button>
            </div>
          ) : (
            /* ── Normal Auth Form ── */
            <>
              <h2 style={{ color: '#f2f2f7', fontSize: '20px', fontWeight: 700, margin: '0 0 4px', letterSpacing: '-0.3px' }}>
                {mode === 'signup' ? 'Create your free account' : 'Welcome back'}
              </h2>
              <p style={{ color: '#98989f', fontSize: '13px', margin: '0 0 24px', lineHeight: 1.55 }}>
                {mode === 'signup'
                  ? 'Get access to real-time GEX levels, AI insights, and market structure.'
                  : 'Sign in to your GammaLens dashboard.'}
              </p>

              {error && (
                <div style={{ background: 'rgba(255,59,48,0.08)', border: '1px solid rgba(255,59,48,0.18)', borderRadius: '9px', padding: '10px 14px', marginBottom: '16px', color: '#ff6b6b', fontSize: '13px' }}>
                  {error}
                  {error.includes('verify') && (
                    <button onClick={handleResendVerification} disabled={resending} style={{ display: 'block', marginTop: '8px', background: 'none', border: 'none', color: '#ff6b6b', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline', textUnderlineOffset: '2px', padding: 0 }}>
                      {resending ? 'Sending...' : 'Resend verification email'}
                    </button>
                  )}
                </div>
              )}
              {success && <AlertBox type="success">{success}</AlertBox>}
              {verifyMessage && <AlertBox type={verifyMessage.type}>{verifyMessage.text}</AlertBox>}

              <form onSubmit={handleSubmit}>
                {mode === 'signup' && (
                  <Field label={<>Display name <span style={{ color: '#71717a' }}>(optional)</span></>}>
                    <input className="auth-input" type="text" value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="How should we call you?" maxLength={50} style={inputStyle} />
                  </Field>
                )}
                <Field label="Email">
                  <input className="auth-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com" required autoComplete="email" style={inputStyle} />
                  {mode === 'signup' && <p style={{ margin: '4px 0 0', color: '#71717a', fontSize: '11px' }}>We'll send a verification link to this email</p>}
                </Field>
                <Field label="Password" style={{ marginBottom: '24px' }}>
                  <div style={{ position: 'relative' }}>
                    <input className="auth-input" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder={mode === 'signup' ? 'At least 6 characters' : 'Your password'} required minLength={6} autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} style={{ ...inputStyle, paddingRight: '52px' }} />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#545459', cursor: 'pointer', fontSize: '12px', fontWeight: 600, fontFamily: 'inherit', padding: '4px' }}>
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  </div>
                </Field>

                <button
                  type="submit"
                  disabled={loading}
                  className="auth-btn-primary"
                  style={{
                    width: '100%', padding: '13px',
                    background: loading ? 'rgba(242,242,247,0.4)' : '#f2f2f7',
                    border: 'none', borderRadius: '11px',
                    color: '#111114',
                    fontSize: '14px', fontWeight: 700,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  }}
                >
                  {loading && (
                    <div style={{ width: '14px', height: '14px', border: '2px solid rgba(17,17,20,.2)', borderTop: '2px solid #111114', borderRadius: '50%', animation: 'auth-spin .7s linear infinite' }} />
                  )}
                  {loading
                    ? (mode === 'signup' ? 'Creating account...' : 'Signing in...')
                    : (mode === 'signup' ? 'Create Free Account' : 'Sign In')}
                </button>
              </form>

              <p style={{ textAlign: 'center', color: '#545459', fontSize: '13px', margin: '20px 0 0' }}>
                {mode === 'signup' ? 'Already have an account?' : "Don't have an account?"}{' '}
                <button className="auth-toggle" onClick={toggleMode} style={{ background: 'none', border: 'none', color: '#98989f', cursor: 'pointer', fontSize: '13px', fontWeight: 600, fontFamily: 'inherit', transition: 'color .15s' }}>
                  {mode === 'signup' ? 'Sign in' : 'Sign up free'}
                </button>
              </p>
            </>
          )}
        </div>

        {/* Skip */}
        {onSkip && !verificationPending && (
          <p style={{ textAlign: 'center', marginTop: '14px' }}>
            <button className="auth-skip" onClick={onSkip} style={{ background: 'none', border: 'none', color: '#71717a', cursor: 'pointer', fontSize: '12px', textDecoration: 'underline', textUnderlineOffset: '3px', fontFamily: 'inherit', transition: 'color .15s' }}>
              Skip for now — browse as guest
            </button>
          </p>
        )}

        {/* Trust badges */}
        {!verificationPending && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '22px', marginTop: '20px', color: '#71717a', fontSize: '11px' }}>
            {[
              { icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>, label: 'Secure' },
              { icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>, label: 'No spam' },
              { icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>, label: '100% free' },
            ].map((b, i) => (
              <span key={i} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {b.icon}{b.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, style = {} }) {
  return (
    <div style={{ marginBottom: '16px', ...style }}>
      <label style={{ display: 'block', color: '#98989f', fontSize: '12px', marginBottom: '7px', fontWeight: 500 }}>{label}</label>
      {children}
    </div>
  );
}

function AlertBox({ type, children }) {
  const isSuccess = type === 'success';
  return (
    <div style={{
      background: isSuccess ? 'rgba(52,199,89,0.07)' : 'rgba(255,59,48,0.07)',
      border: `1px solid ${isSuccess ? 'rgba(52,199,89,0.2)' : 'rgba(255,59,48,0.18)'}`,
      borderRadius: '9px', padding: '10px 14px', marginBottom: '14px',
      color: isSuccess ? '#34c759' : '#ff6b6b', fontSize: '13px', lineHeight: 1.5,
    }}>{children}</div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '11px 14px',
  background: '#0f0f12',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '10px',
  color: '#f2f2f7',
  fontSize: '16px',
  outline: 'none',
  transition: 'border-color 0.2s, box-shadow 0.2s',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};
